import {
  DECISION_LABEL,
  SPECIALIST_OUTCOMES,
  SPECIALIST_OUTCOME_LABEL,
  type ReviewSteps as Steps,
  type SpecialistOutcome,
} from '@cyberscore/contracts';
import { CheckCircle2, Circle, ListChecks, UserCheck } from 'lucide-react';
import { useState } from 'react';
import { ApiError } from '../../lib/api';
import { useSession } from '../../lib/session';
import { useToast } from '../../ui/Toasts';
import { useAnswerProposal, useSpecialistReview, useWithdrawProposal } from './api';

const when = (iso: string) =>
  new Date(iso).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' });
const errorText = (e: unknown, fallback: string) =>
  e instanceof ApiError ? (e.issues[0]?.message ?? e.message) : fallback;

/**
 * The steps an industry's review takes before cover is decided (task 80): a specialist review, evidence
 * for named controls, and a second underwriter's sign-off. Shown above the decision.
 */
export function ReviewStepsCard({ id, steps, canWrite }: { id: string; steps: Steps; canWrite: boolean }) {
  const s = steps.specialist;
  const missing = steps.evidence.filter((e) => !e.evidenced);
  if (!s && !steps.evidence.length && steps.sign_offs < 2) return null;
  return (
    <section className="card pad" aria-labelledby={`steps-${id}`}>
      <h2 id={`steps-${id}`} className="section-title row gap">
        <ListChecks size={17} /> {steps.profile ? `${steps.profile} review` : 'Review steps'}
      </h2>
      <ul className="steps-list">
        {s && (
          <Step done={!s.because.length || !!s.review}>
            <b>{s.label} review</b>
            {!s.because.length ? (
              <span className="muted small">Not needed: everything the {s.label.toLowerCase()} watches is in place.</span>
            ) : s.review ? (
              <span className="muted small">
                {SPECIALIST_OUTCOME_LABEL[s.review.outcome]}, by {s.review.reviewed_by_name ?? 'a colleague'} on {when(s.review.reviewed_at)}: “{s.review.note}”
              </span>
            ) : (
              <span className="muted small">Needed before deciding: {s.because.join(', ')} not fully in place.</span>
            )}
          </Step>
        )}
        {steps.evidence.length > 0 && (
          <Step done={!missing.length}>
            <b>Evidence before acceptance</b>
            <span className="muted small">
              {missing.length
                ? `${missing.map((e) => e.control).join(', ')} still ${missing.length === 1 ? 'rests' : 'rest'} on the business’s word.`
                : `${steps.evidence.map((e) => e.control).join(', ')} evidenced.`}
            </span>
          </Step>
        )}
        {steps.sign_offs > 1 && (
          <Step done={false}>
            <b>Two underwriters sign the decision</b>
            <span className="muted small">One proposes it; another confirms it or sends it back.</span>
          </Step>
        )}
      </ul>
      {canWrite && s && s.because.length > 0 && !s.review && <SpecialistForm id={id} label={s.label} />}
      {steps.proposal && <Proposal id={id} steps={steps} canWrite={canWrite} />}
    </section>
  ); // prettier-ignore
}

function Step({ done, children }: { done: boolean; children: React.ReactNode }) {
  return (
    <li className={`step-row${done ? ' done' : ''}`}>
      {done ? <CheckCircle2 size={16} className="ok-t" /> : <Circle size={16} className="muted" />}
      <span className="stack-xs">{children}</span>
    </li>
  );
}

function SpecialistForm({ id, label }: { id: string; label: string }) {
  const record = useSpecialistReview(id);
  const toast = useToast();
  const [outcome, setOutcome] = useState<SpecialistOutcome>('no_concerns');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  return (
    <form
      className="stack-sm mt"
      aria-label={`${label} review`}
      onSubmit={async (e) => {
        e.preventDefault();
        setError(null);
        try {
          await record.mutateAsync({ outcome, note: note.trim() });
          toast(`${label} review recorded`, 'ok');
        } catch (err) {
          setError(errorText(err, 'Could not record it'));
        }
      }}
    >
      <div className="small"><b>Record the {label.toLowerCase()} review</b></div>
      <div className="row gap wrap" role="radiogroup" aria-label="Outcome">
        {SPECIALIST_OUTCOMES.map((o) => (
          <label key={o} className="small row gap">
            <input type="radio" name={`spec-${id}`} checked={outcome === o} onChange={() => setOutcome(o)} /> {SPECIALIST_OUTCOME_LABEL[o]}
          </label>
        ))}
      </div>
      <textarea className="input" rows={2} aria-label="What the specialist found" maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What you checked and found" />
      {error && <div className="notice" role="alert">{error}</div>}
      <button type="submit" className="btn btn-sm" disabled={!note.trim() || record.isPending}>Record review</button>
    </form>
  ); // prettier-ignore
}

function Proposal({ id, steps, canWrite }: { id: string; steps: Steps; canWrite: boolean }) {
  const p = steps.proposal!;
  const { me } = useSession();
  const answer = useAnswerProposal(id);
  const withdraw = useWithdrawProposal(id);
  const toast = useToast();
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const mine = me?.user.id === p.proposed_by;
  const act = async (f: () => Promise<unknown>, done: string) => {
    setError(null);
    try {
      await f();
      toast(done, 'ok');
    } catch (err) {
      setError(errorText(err, 'Could not do that'));
    }
  };
  return (
    <div className="proposal mt" role="region" aria-label="Proposed decision">
      <div className="row gap"><UserCheck size={16} /> <b>Proposed: {DECISION_LABEL[p.outcome]}</b></div>
      <p className="small">By {p.proposed_by_name ?? 'a colleague'} on {when(p.proposed_at)}. “{p.reason}”</p>
      {p.conditions.length > 0 && (
        <ul className="small">{p.conditions.map((c, i) => <li key={i}>{c.title} (due {c.due_date})</li>)}</ul>
      )}
      {canWrite && (mine ? (
        <button type="button" className="btn btn-sm" disabled={withdraw.isPending} onClick={() => act(() => withdraw.mutateAsync(), 'Proposal withdrawn')}>Withdraw it</button>
      ) : (
        <div className="stack-sm">
          <textarea className="input" rows={2} aria-label="Note to the proposer" maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Needed to send it back; optional to confirm" />
          <div className="row gap">
            <button type="button" className="btn btn-primary btn-sm" disabled={answer.isPending} onClick={() => act(() => answer.mutateAsync({ action: 'confirm', ...(note.trim() ? { note: note.trim() } : {}) }), 'Decision confirmed')}>Confirm: {DECISION_LABEL[p.outcome]}</button>
            <button type="button" className="btn btn-sm" disabled={answer.isPending || !note.trim()} onClick={() => act(() => answer.mutateAsync({ action: 'return', note: note.trim() }), 'Sent back')}>Send back</button>
          </div>
        </div>
      ))}
      {error && <div className="notice" role="alert">{error}</div>}
    </div>
  ); // prettier-ignore
}
